// Hold the current cart via the v2 /cart/commit endpoint.
//
// Replaces the legacy 3-call dance (POST /receipts → loop POST /items →
// PUT /receipts/{id}{on_hold:true}) with a single atomic commit. The BE
// creates the receipt + items + JTs and flips on_hold=true inside one
// transaction with finalize(strict=True).
//
// Same external shape as before: `{ holdCurrent, isPending }`. Existing
// callers (LeftSidebar, FunctionsTab) don't need changes.

import { usePOSStore, isReceiptPartiallyPaid } from '@/stores/posStore'
import { useCartStore } from '@/stores/cartStore'
import { useUIStore } from '@/stores/uiStore'
import { useSessionStore } from '@/stores/sessionStore'
import { useCommitCart, buildCommitRequest } from '@/api/cart'
import { printReceiptViaPreferredPath } from '@/api/payments'
import { V2ApiError, v2ErrorCode } from '@/api/v2'

export function useHoldCurrentReceipt() {
  const { cart, currentReceiptId, clearReceipt, serverTotals, isRefundMode } = usePOSStore()
  const { showToast, openModal, showConfirm, setProcessingAction } = useUIStore()
  const commit = useCommitCart()

  const isPending = commit.isPending

  const holdCurrent = async () => {
    // Empty cart + no in-flight receipt -> the button doubles as a "recall
    // held receipt" trigger. Matches v1 behaviour so muscle memory stays.
    if (cart.length === 0 && !currentReceiptId) {
      openModal('held-receipts')
      return
    }
    if (cart.length === 0) {
      showToast({
        type: 'warning',
        title: 'Empty Cart',
        message: 'Add items before holding a receipt.',
      })
      return
    }

    // Don't hold a receipt that has money on it but isn't closed: a partial
    // tender (>=1 payment recorded, balance still owed) must be completed or
    // voided first -- suspending a sale with a payment against it breaks
    // drawer/audit reconciliation (standard POS practice). partialPayments is
    // the primary signal (sourced from the receipt's payments, survives cart
    // edits); isReceiptPartiallyPaid(serverTotals) is the persisted backstop
    // for a post-refresh hold before the CheckoutModal mirror has
    // re-populated partialPayments. Refund mode is excluded: its negative
    // amount_to_pay makes the mirror skip partialPayments, and the predicate
    // returns false.
    const hasOpenTender =
      (useCartStore.getState().partialPayments?.length ?? 0) > 0
      || isReceiptPartiallyPaid(serverTotals, isRefundMode)
    if (hasOpenTender) {
      showToast({
        type: 'warning',
        title: 'Payment Applied',
        message: 'Complete or void the payment in Checkout before holding this receipt.',
      })
      return
    }

    setProcessingAction(true)
    try {
      // The bridge keeps cartStore.lines + customer in sync with posStore
      // on every mutation, so by the time the cashier hits Hold the v2
      // cart is already a faithful mirror. Read once and ship.
      const v2Cart = useCartStore.getState()
      const req = buildCommitRequest(v2Cart, { hold: true }, { receiptId: currentReceiptId })

      const result = await commit.mutateAsync(req)

      // Server returns the canonical receipt (with its real number) inside
      // the v2 envelope's `data.receipt`. Pull the id so we can fire the
      // print-on-hold slip; the rest of the flow doesn't care about it.
      const receipt = (result?.data as { receipt?: { id?: number } } | undefined)?.receipt
      const newReceiptId = receipt?.id ?? null

      // Local-print registers: prompt the cashier to print a hold slip
      // (some holds don't need a physical tag). Server-print registers
      // keep auto-printing -- there's no local printer to babysit.
      // Failures in either case don't roll back the hold.
      if (newReceiptId) {
        const localPrint = !!useSessionStore.getState().selectedRegister?.local_printing_enabled
        // On the local-print path the cashier explicitly clicks Print, so
        // surface failures with a toast -- swallowing them would let them
        // walk away believing a slip is printing when it isn't. On the
        // server-print path the print is implicit (no prompt), so keep
        // it best-effort to stay quiet on the happy path.
        const firePrint = (notifyOnError: boolean) => {
          printReceiptViaPreferredPath({
            receiptId: newReceiptId,
            serverEndpoint: 'print-on-hold',
          }).catch(() => {
            if (notifyOnError) {
              showToast({
                type: 'error',
                title: 'Print Failed',
                message: 'Could not print the hold slip.',
              })
            }
          })
        }
        if (localPrint) {
          showConfirm({
            title: 'Print Hold Slip?',
            message: 'Print a slip for the held receipt?',
            confirmLabel: 'Print',
            cancelLabel: 'Skip',
            onConfirm: () => firePrint(true),
          })
        } else {
          firePrint(false)
        }
      }

      clearReceipt()
      useCartStore.getState().clear()
      showToast({
        type: 'success',
        title: 'Receipt Held',
        message: 'Use Recall Hold to resume.',
      })
    } catch (err) {
      // A refused / throttled manager PIN must stop riding the requests;
      // a 409 over the limit drops that tuple's approval (same as the
      // preview bridge and CheckoutModal).
      useCartStore.getState().handleGateError(
        v2ErrorCode(err),
        err instanceof V2ApiError ? err.extra : null,
      )
      // V2ApiError carries the structured envelope -- surface its message
      // when present so the cashier sees the real reason (sanity_check,
      // out-of-stock, etc.) instead of a generic "Hold Failed".
      const message =
        err instanceof V2ApiError
          ? err.message
          : 'Could not hold the receipt.'
      showToast({ type: 'error', title: 'Hold Failed', message })
    } finally {
      setProcessingAction(false)
    }
  }

  return { holdCurrent, isPending }
}
